import {
  CheckCircleFilled,
  ExclamationCircleFilled,
  InfoCircleOutlined,
  LoadingOutlined,
  WarningFilled,
} from '@ant-design/icons';
import {
  type AdvisorIdentity,
  buildFieldSections,
  buildSignerRoster,
  type FieldSectionInfo,
  renderInstanceLabel,
  resolveSignerContact,
  roleExpectsSignature,
  rolesWithNothingToSign,
} from '@bmo/form-core';
import { Select, Tooltip, Typography } from 'antd';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type {
  EmailVerifyResult,
  EmailVerifyStatus,
  SignatoryRole,
  WorkflowStep,
  WorkflowTemplate,
} from '@/api/workflows';
import { workflowsApi } from '@/api/workflows';
import { useSession } from '@/auth/useSession';

import { cacheVerdict, readCachedVerdict } from './emailVerifyCache';
import { normalizeEmail } from './emailVerifyGate';
import { eSignEmailGate } from './esignEmailGate';
import type { FieldValue } from './FieldRenderer';
import { displayPhone } from './fieldValidation';

const { Title, Paragraph, Text } = Typography;

/** The two modes BMO offers per signatory. */
export const ESIGN_MODES = ['remote', 'in-person'] as const;
export type ESignMode = (typeof ESIGN_MODES)[number];

/**
 * Value key holding one signatory's chosen eSign mode.
 *
 * Namespaced so it can never collide with a real field id (those are GUIDs),
 * and instance-qualified so a role mapped into a REPEATABLE section gets one
 * choice per signer rather than one for the whole role — the same bug class
 * that has bitten signer resolution, reveals and review before now.
 */
export function esignModeKey(roleSlug: string, instanceIdx: number): string {
  return `esignMode:${roleSlug}:${instanceIdx}`;
}

/** The stored mode for one signatory, or null when nothing valid is set. */
export function readESignMode(
  values: Record<string, FieldValue>,
  roleSlug: string,
  instanceIdx: number,
): ESignMode | null {
  const raw = values[esignModeKey(roleSlug, instanceIdx)];
  return typeof raw === 'string' && ESIGN_MODES.includes(raw as ESignMode)
    ? (raw as ESignMode)
    : null;
}

/**
 * How many signatories still have no mode chosen. Zero means the advisor may
 * proceed — the ticket's "when a mode for esign is selected for all client
 * signatories, advisor should be able to generate initiate esign".
 *
 * Counts the advisor too: they select a mode for themselves.
 */
export function countSignersMissingMode(
  signers: { role: { slug: string }; instanceIdx: number }[],
  values: Record<string, FieldValue>,
): number {
  return signers.filter(
    ({ role, instanceIdx }) => readESignMode(values, role.slug, instanceIdx) === null,
  ).length;
}

/**
 * Read-only preview of the SIGNATORY package BMO will ship to OneSpan once
 * the advisor finishes filling the form. Resolves each signer's contact info
 * from the originator's authored config:
 *   - Applicant: pulls Name / Email / Phone values from the mapped form
 *     fields (so the originator can verify in preview that the right
 *     person's data flows into the SIGNATORY payload).
 *   - Advisor: pulls from the BFF/JWT session (name + email from /me).
 * Per-PDF signature placements are then listed below — same shape as the
 * originator authored in the OneSpanConfig drawer.
 *
 * No interactive controls: this is a runtime preview, not the actual
 * signing surface (BMO + OneSpan own that downstream). The advisor
 * clicks "Next" to move past it.
 */
export function OneSpanESignStep({
  template,
  step,
  values,
  language,
  mode = 'advisor',
  onSetStepBlocker,
  // `onChange` is consumed only by the eSign Type selector, which is fixed to Remote eSign
  // and disabled for now. Kept in the props contract (FormShell still passes it) so
  // restoring the choice is a local change.
  // onChange,
}: {
  template: WorkflowTemplate;
  step: WorkflowStep;
  values: Record<string, FieldValue>;
  language: 'en' | 'fr';
  /** Both modes resolve the signatory list and both fire ZeroBounce (the
   *  per-email {@link ./emailVerifyCache} is what keeps a repeated
   *  dashboard-preview click from re-spending credits — and unlike the ref it
   *  replaced, it survives the remount such a click causes).
   *  What differs is the GATE: an advisor cannot
   *  advance until ZeroBounce has returned a verdict for every address,
   *  whereas preview lets an unverifiable address through so an outage can't
   *  wedge the authoring surface. See {@link eSignEmailGate}. */
  mode?: 'preview' | 'advisor';
  /** Set by FormShell — the OneSpan step blocks Next when any signatory email
   *  fails ZeroBounce verification, or (advisor only) when ZeroBounce never
   *  returned a verdict at all (INFLOWS-188). Called with a tooltip string
   *  when blocking; called with null on unmount. */
  onSetStepBlocker?: (reason: string | null) => void;
  /** Writes the per-signatory eSign mode back into FormShell's value store.
   *  The signatory CONTACT rows stay read-only per the ticket ("non editable
   *  from the summary page") — the mode selector is the one control here.
   *
   *  Currently unread: the eSign Type Select is disabled and fixed to Remote eSign,
   *  so the step writes nothing and is read-only everywhere. Kept in the contract
   *  (FormShell still passes it, withheld when locked) for when the choice returns. */
  onChange?: (key: string, next: FieldValue, opts?: { programmatic?: boolean }) => void;
}) {
  const { t } = useTranslation();
  const { user } = useSession();

  // Originator-authored step title + description. Bold title sits flush at
  // the top to match the other step renderers (Application Review etc.);
  // description is a sub-line in slate-700.
  const title = language === 'fr' ? step.titleFr : step.titleEn;
  const description = language === 'fr' ? step.descriptionFr : step.descriptionEn;

  const config = step.oneSpanConfig;

  // Where each authored field lives, so a role mapped into a REPEATABLE
  // section can be expanded into one signer per instance. Without this the
  // resolver reads the bare `values[fieldId]`, which only ever exists for
  // instance 0 — i.e. a second Accountholder never reached the signing
  // package at all.
  const sectionByField = useMemo(() => buildFieldSections(template), [template]);

  // One entry per (role, instance): visible roles (gating conditions applied)
  // expanded by instance count. THE SAME roster `buildESignSubmission` sends to
  // BMO — the table an advisor approves and the package BMO builds have to be
  // the same list of people.
  //
  // That is also why `rolesWithNothingToSign` is applied here: a signatory whose
  // every signature placement was itself conditioned out comes off both lists or
  // neither, never one.
  //
  // `roleExpectsSignature` is the ONE place the two lists part company, and
  // deliberately: a role the originator marked as expecting no signatures is a
  // party on the application, not a signer. Its details still travel to BMO in
  // `applicantInfo` — which is why `buildESignSubmission` keeps it — but this
  // table is the signing package, so it has no row here, no eSign mode and no
  // ZeroBounce check gating Next on an address nobody will send a package to.
  //
  // Memoized because `resolvedEmails` below depends on it, and a fresh array
  // every render made that memo a no-op.
  const nothingToSign = useMemo(
    () => rolesWithNothingToSign(template, values, language),
    [template, values, language],
  );
  const signers = useMemo(
    () =>
      buildSignerRoster(config?.roles, sectionByField, values, nothingToSign).filter(({ role }) =>
        roleExpectsSignature(role),
      ),
    [config, sectionByField, values, nothingToSign],
  );

  // Every signatory — client AND advisor — picks a mode for themselves, per
  // the ticket ("BMO Advisor should be allowed to select a mode of esign for
  // self"). Counted here rather than inside the render so the Next-blocker
  // effect below can depend on a primitive.
  //
  // Commented out for now: the eSign Type column is fixed to Remote eSign and
  // disabled, so there is no choice left to gate on — and with nothing writing a
  // mode into the value store, this gate would block Next forever. Restore it
  // together with the selectable column in SignerRow.
  // const missingModeCount = useMemo(
  //   () => countSignersMissingMode(signers, values),
  //   [signers, values],
  // );

  // Resolve the email address for every visible signatory ONCE per render
  // so both the SignerRow render and the verify effect below see the same
  // list.
  //
  // DELIBERATE: verification is NOT conditioned on the chosen eSign mode.
  // Confirmed by BMO 2026-07-30 — "in person or remote we need verify that
  // email always". An in-person signer still needs a deliverable address for
  // the signer record and audit trail, so do not add a `mode === 'remote'`
  // short-circuit here as an optimisation.
  //
  // Emails include the advisor's session email — advisors mistype
  // their own account email far less often, but ZeroBounce catches it if
  // the whole account is bad. Empty session emails (some dev-login paths)
  // are coerced to null so the resolver treats them as absent — a real
  // Entra session always carries a non-empty address.
  const advisorEmail = user?.email && user.email.trim() !== '' ? user.email : null;

  // The advisor's own row is filled from the session, not the form. Memoized so
  // it stays a stable dependency for the email memo below.
  const advisor: AdvisorIdentity = useMemo(
    () => ({
      fullName: user?.name ?? user?.preferred_username ?? null,
      firstName: user?.firstName ?? null,
      lastName: user?.lastName ?? null,
      email: advisorEmail,
      phone: user?.phoneNumber ?? null,
    }),
    [user, advisorEmail],
  );

  const resolvedEmails = useMemo(() => {
    const out: string[] = [];
    for (const { role, instanceIdx } of signers) {
      const resolved = resolveSignerContact(role, values, sectionByField, instanceIdx, advisor);
      if (resolved.email) out.push(normalizeEmail(resolved.email));
    }
    return Array.from(new Set(out)); // dedupe — same email in two roles = one credit
  }, [signers, values, sectionByField, advisor]);

  // Verify effect. Fires on step entry (mount) and re-fires when the
  // resolved emails change (advisor edited a mapped form field on a prior
  // step). The per-email cache is `emailVerifyCache` — a MODULE singleton, not
  // a ref: this effect firing on mount is exactly what made a step re-visit
  // re-batch every signatory, and a cache that died with the component could
  // not fix that. It is shared with the ordinary-email-field store
  // (`useEmailVerification`), so an address already checked on a form field
  // costs nothing to show here.
  const [verifyResults, setVerifyResults] = useState<Record<string, EmailVerifyResult>>({});
  const [verifying, setVerifying] = useState(false);

  // Stable string key for the effect deps — resolvedEmails is a new
  // array reference every render (visibleRoles / signers aren't
  // referentially stable), which would fire this effect on every render
  // and hammer the endpoint into a 429 rate-limit trip. Comparing the
  // JOINED value string dedupes those spurious renders — the effect
  // only fires when the actual SET of emails changes.
  const resolvedEmailsKey = resolvedEmails.join('|');

  useEffect(() => {
    // Empty email set (no mapped fields yet) → nothing to check.
    // Preview mode ALSO fires ZeroBounce because that's how we test the
    // integration today — real advisor mode isn't wired in the FE yet.
    // The shared cache still prevents credit re-spending on step re-visits,
    // so the cost stays bounded.
    if (resolvedEmails.length === 0) return;

    const toVerify = resolvedEmails.filter((e) => !readCachedVerdict(e));
    // Preload the state from the cache so a re-mount shows previous results
    // instantly while any newly-added emails are still in flight.
    const initial: Record<string, EmailVerifyResult> = {};
    for (const e of resolvedEmails) {
      const cached = readCachedVerdict(e);
      if (cached) initial[e] = cached;
    }
    setVerifyResults(initial);
    if (toVerify.length === 0) return;

    let cancelled = false;
    setVerifying(true);
    workflowsApi
      .verifyEmails(toVerify)
      .then((results) => {
        // Cached BEFORE the cancel check. A verdict we have paid for is worth keeping
        // whether or not this effect is still the live one — the advisor stepping away
        // mid-request is ordinary, and discarding it there would re-bill on re-entry.
        // `cacheVerdict` drops `Unknown` itself; see that module for why that matters
        // more here than anywhere else (an `Unknown` hard-blocks this step).
        for (const r of results) cacheVerdict(r);
        if (cancelled) return;
        setVerifyResults((prev) => {
          const next = { ...prev };
          for (const r of results) next[normalizeEmail(r.email)] = r;
          return next;
        });
      })
      .finally(() => {
        if (!cancelled) setVerifying(false);
      });

    return () => {
      cancelled = true;
    };
    // resolvedEmailsKey is the stable dep — resolvedEmails is read inside
    // via closure but only iterated within the effect body, so we don't
    // need it in deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, resolvedEmailsKey]);

  // Report the Next blocker to FormShell. The RULE lives in `eSignEmailGate`
  // (read its doc comment for why each status falls where it does); this
  // effect only maps the verdict onto a message.
  //
  // The short version: an advisor may not leave this step until ZeroBounce has
  // actually spoken about every signatory address. Invalid blocks, and as of
  // 2026-10-06 so does Unknown — which is every failure mode there is
  // (outage, missing key, 429, 4xx, address not returned) wearing one name.
  // CatchAll / SpamTrap / DoNotMail keep their amber glyph and PASS: approval
  // here means the package can be delivered, not that the mailbox is reputable.
  useEffect(() => {
    if (!onSetStepBlocker) return;
    const verdict = eSignEmailGate({
      mode,
      emails: resolvedEmails,
      results: verifyResults,
      verifying,
    });
    if (verdict.kind === 'checking') {
      onSetStepBlocker(
        t('formRenderer.eSign.emailVerify.checking', {
          defaultValue: 'Checking signatory emails…',
        }),
      );
      return;
    }
    if (verdict.kind === 'invalid') {
      onSetStepBlocker(
        t('formRenderer.eSign.emailVerify.nextBlocked', {
          count: verdict.count,
          defaultValue: 'Fix the invalid signatory email(s) before continuing.',
        }),
      );
      return;
    }
    if (verdict.kind === 'unverified') {
      // Terminal by design — BMO chose a hard block with no override, so there
      // is no "continue anyway". Leaving and re-entering the step retries,
      // because the cache below refuses to store a non-verdict.
      onSetStepBlocker(
        t('formRenderer.eSign.emailVerify.nextBlockedUnverified', {
          count: verdict.count,
          defaultValue: "We couldn't verify the signatory email(s). Try again before continuing.",
        }),
      );
      return;
    }
    // "When a mode for esign is selected for all client signatories, advisor
    // should be able to generate initiate esign" — until then Next stays
    // disabled. Checked AFTER the email gate so the more specific email
    // message wins when both apply.
    //
    // Commented out with the mode CHOICE — see `missingModeCount` above.
    // if (missingModeCount > 0) {
    //   onSetStepBlocker(
    //     t('formRenderer.eSign.mode.nextBlocked', {
    //       count: missingModeCount,
    //       defaultValue: 'Select an eSign mode for every signatory before continuing.',
    //     }),
    //   );
    //   return;
    // }
    onSetStepBlocker(null);
    // Only clear on unmount — an in-place cleanup would flicker the
    // blocker off/on during every result-arrival tick, giving a fast
    // clicker a one-render window where Next is enabled.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onSetStepBlocker, mode, resolvedEmailsKey, verifyResults, verifying, t]);

  // Dedicated unmount cleanup so the step-blocker is released when the
  // advisor navigates away — kept separate from the setter-effect above
  // so a mid-render dep change doesn't accidentally trigger a stale null.
  useEffect(() => {
    return () => {
      if (onSetStepBlocker) onSetStepBlocker(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Title level={3} style={{ margin: 0, fontWeight: 700, color: '#111827' }}>
        {title}
      </Title>
      {description && (
        <Paragraph style={{ marginBottom: 0, color: '#374151', fontSize: 14 }}>
          {description}
        </Paragraph>
      )}

      {!config || config.roles.length === 0 ? (
        <EmptyCard message={t('formRenderer.eSign.notConfigured')} />
      ) : (
        <>
          {/* 2026-07-27 BMO design: a striped table, one row per signatory —
              Name / Role / Email / Mobile / eSign Mode (BMO story GAMXDPT-345;
              the Role column was added after the Figma, which had none) — replacing the
              bordered per-signer cards. The contact resolution, the ZeroBounce
              verdict and the Next-blocking behaviour are unchanged; only the
              presentation moved.

              Dealer Code and Representative Code were removed outright. eSign
              Type still renders, but disabled and fixed to Remote eSign — see
              SignerRow. */}
          <div className="bmo-esign-table" role="table">
            <div className="bmo-esign-table__head" role="row">
              <span role="columnheader">{t('formRenderer.eSign.colName')}</span>
              <span role="columnheader">{t('formRenderer.eSign.colRole')}</span>
              <span role="columnheader">
                {t('formRenderer.eSign.colEmail')}
                <ColumnHelp
                  column={t('formRenderer.eSign.colEmail')}
                  help={t('formRenderer.eSign.colEmailHelp')}
                />
              </span>
              <span role="columnheader">
                {t('formRenderer.eSign.colPhone')}
                <ColumnHelp
                  column={t('formRenderer.eSign.colPhone')}
                  help={t('formRenderer.eSign.colPhoneHelp')}
                />
              </span>
              <span role="columnheader">{t('formRenderer.eSign.colType')}</span>
            </div>
            {signers.map(({ role, instanceIdx }) => (
              <SignerRow
                key={`${role.slug}:${instanceIdx}`}
                role={role}
                instanceIdx={instanceIdx}
                roleLabel={signerRoleLabel(
                  role,
                  instanceIdx,
                  template,
                  sectionByField,
                  language,
                  t,
                )}
                // onChange={onChange}
                sectionByField={sectionByField}
                values={values}
                advisor={advisor}
                t={t}
                verifyResults={verifyResults}
                verifying={verifying}
                mode={mode}
                formatPhones={template.formatPhoneNumbers === true}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ---- Sub-components --------------------------------------------------------

function SignerRow({
  role,
  instanceIdx,
  roleLabel,
  // onChange,
  sectionByField,
  values,
  advisor,
  t,
  verifyResults,
  verifying,
  mode,
  formatPhones = false,
}: {
  role: SignatoryRole;
  /** 0 for a normal signer; the repeatable-section instance otherwise. */
  instanceIdx: number;
  /** What the Role column shows — see {@link signerRoleLabel}. */
  roleLabel: string;
  /** Absent in preview and on a locked application. Unread while the eSign Type Select is
   *  fixed to Remote eSign — it is disabled unconditionally, not on this prop. */
  onChange?: (key: string, next: FieldValue, opts?: { programmatic?: boolean }) => void;
  sectionByField: Map<string, FieldSectionInfo>;
  values: Record<string, FieldValue>;
  /** Session-sourced identity for the advisor row; ignored on client rows. */
  advisor: AdvisorIdentity;
  t: (key: string, opts?: Record<string, unknown>) => string;
  /** Verify results keyed by normalized (trim+lowercase) email address. */
  verifyResults: Record<string, EmailVerifyResult>;
  verifying: boolean;
  /** Only read by {@link EmailVerifyIcon} — an unverifiable address is a dead
   *  end for an advisor and a shrug for an originator, and the glyph has to
   *  say which. */
  mode: 'preview' | 'advisor';
  /** The workflow's opt-in {@link WorkflowTemplate.formatPhoneNumbers}. */
  formatPhones?: boolean;
}) {
  const resolved = resolveSignerContact(role, values, sectionByField, instanceIdx, advisor);
  // A signed-in advisor whose session carries no display name still gets a row;
  // the placeholder is display-only and never reaches the signing package.
  const displayName =
    resolved.fullName || (role.type === 'advisor' ? t('formRenderer.eSign.signedInAdvisor') : '');

  // Commented out with the eSign Type CHOICE below — the column renders, fixed to
  // Remote eSign, so nothing reads or writes the stored mode today.
  // const modeKey = esignModeKey(role.slug, instanceIdx);
  // const selectedMode = readESignMode(values, role.slug, instanceIdx) ?? undefined;
  const normalizedEmail = resolved.email ? normalizeEmail(resolved.email) : null;
  const emailVerification = normalizedEmail ? verifyResults[normalizedEmail] : undefined;

  return (
    <div className="bmo-esign-table__row" role="row">
      {/* Name and role in their own columns, per BMO's story (GAMXDPT-345). The
          signing order is still not shown — rows are already in that order. */}
      <Cell label={t('formRenderer.eSign.colName')}>
        <Text strong style={{ fontSize: 14, color: '#111827' }}>
          {displayName || t('formRenderer.eSign.unnamedSigner')}
        </Text>
      </Cell>
      <Cell label={t('formRenderer.eSign.colRole')}>
        <Text style={{ fontSize: 14, color: '#111827' }}>{roleLabel}</Text>
      </Cell>
      {/* Values are resolved from the form, not typed here — the boxed styling
          matches the design without implying they're editable. */}
      {/* Verification reads as a green/red glyph pinned to the right inside
          the field, per the client's Figma — not a coloured message block
          underneath. The wording (and any "did you mean" suggestion) moves
          into the icon's tooltip so the reason is still reachable, and the
          Next-blocking behaviour is unchanged. */}
      <Cell label={t('formRenderer.eSign.colEmail')} help={t('formRenderer.eSign.colEmailHelp')}>
        <div className="bmo-esign-table__value">
          <span className="bmo-esign-table__value-text">
            {resolved.email || <span className="bmo-esign-table__empty">—</span>}
          </span>
          {resolved.email && (
            <EmailVerifyIcon verifying={verifying} result={emailVerification} mode={mode} t={t} />
          )}
        </div>
      </Cell>
      <Cell label={t('formRenderer.eSign.colPhone')} help={t('formRenderer.eSign.colPhoneHelp')}>
        <div className="bmo-esign-table__value">
          <span className="bmo-esign-table__value-text">
            {(formatPhones ? displayPhone(resolved.phone) : resolved.phone) || (
              <span className="bmo-esign-table__empty">—</span>
            )}
          </span>
        </div>
      </Cell>
      {/* eSign Type — shown, but not a choice for now: Remote eSign is the only
          option and the Select is disabled, so the whole row is read-only again
          ("non editable from the summary page"). The value is hard-coded rather
          than read from the store, because with in-person unavailable a mode
          left over from an earlier save must not surface as the current one.
          Restoring the choice means reading `selectedMode`, wiring
          `onChange`/`modeKey` back, re-adding the in-person option, and
          un-commenting the `missingModeCount` Next-gate above. */}
      <Cell label={t('formRenderer.eSign.colType')}>
        <Select
          value="remote"
          disabled
          // The SAME text the Name cell shows, not the raw `resolved.fullName`. That field is null
          // whenever the mapped name field is still blank — and for the advisor row, which is
          // filled from the session — so the name interpolated to nothing and every such row
          // announced as a bare "eSign mode for". Falling back the way the visible cell does keeps
          // the control named after the person the row is actually about.
          aria-label={t('formRenderer.eSign.mode.ariaLabel', {
            name: displayName || t('formRenderer.eSign.unnamedSigner'),
          })}
          style={{ width: '100%' }}
          options={[{ value: 'remote', label: t('formRenderer.eSign.mode.remote') }]}
        />
      </Cell>
    </div>
  );
}

/**
 * One cell, carrying its own column name.
 *
 * The label is the HEADER ROW's understudy — both are in the markup and CSS
 * shows exactly one: the shared header at table width, these at the width where
 * the row stacks and that header is gone (`@container form-shell (max-width:
 * 900px)` in global.css). Same arrangement as the repeatable table's
 * `.bmo-repeatable-cell-label--table`, and the same reason: a stacked row of
 * two identically-boxed values is unreadable without saying which is the email
 * and which the phone — both read "—" when the form hasn't filled them.
 *
 * Which is also why `help` exists here. The tooltips explaining where these
 * values come from live on the header, so hiding the header took them with it;
 * the Figma's stacked layout shows the ⓘ beside the row label instead.
 *
 * Deliberately NOT `aria-hidden`: `display: none` keeps the label out of the
 * accessibility tree at table width (where the real `columnheader`s name the
 * cells), and the same property has removed those headers by the time the
 * labels appear. Exactly one naming is exposed either way, never both.
 */
function Cell({
  label,
  help,
  children,
}: {
  label: string;
  /** Tooltip copy for the ⓘ beside the label; omitted on columns without one. */
  help?: string;
  children: ReactNode;
}) {
  return (
    <div className="bmo-esign-table__cell" role="cell">
      <div className="bmo-esign-table__cell-label">
        <span>{label}</span>
        {help && <ColumnHelp column={label} help={help} />}
      </div>
      {children}
    </div>
  );
}

/**
 * The ⓘ beside a column name, and the only route to the copy explaining where that column's
 * values come from.
 *
 * <h3>Why it is a button</h3>
 * It was a bare `<InfoCircleOutlined>` inside a Tooltip. A `<span>` is not focusable, and AntD's
 * default trigger is hover alone, so the explanation was reachable with a mouse and by no other
 * means — not by keyboard, not by touch. A real `<button>` with `focus` in the trigger list puts
 * it in the tab order and shows the same copy on focus.
 *
 * <h3>Why the glyph is aria-hidden</h3>
 * An @ant-design/icons glyph renders as `<span role="img" aria-label="info-circle">`, and it sat
 * INSIDE the `columnheader` — so the column announced as "Email info-circle", and in the stacked
 * layout every cell label did the same. The icon is decoration; the button beside it carries the
 * real name.
 *
 * The tooltip copy is not duplicated into `aria-label`: the label says what the control IS
 * ("About the Email column") and the tooltip is its content, which is the division a screen
 * reader expects. Naming it with the whole sentence would read the explanation twice.
 */
function ColumnHelp({
  column,
  help,
}: {
  /** The column this explains, for the button's accessible name. */
  column: string;
  help: string;
}) {
  // Its own translator rather than one threaded down: `Cell` renders this too and has no `t` of
  // its own, and passing one through purely to reach here would be a prop on every call site.
  const { t } = useTranslation();
  return (
    <Tooltip title={help} trigger={['hover', 'focus']}>
      <button
        type="button"
        className="bmo-esign-table__info-btn"
        aria-label={t('formRenderer.eSign.colHelpAria', { column })}
      >
        <InfoCircleOutlined className="bmo-esign-table__info" aria-hidden="true" />
      </button>
    </Tooltip>
  );
}

/**
 * Verification glyph pinned to the right edge of the email field.
 *
 * The 2026-07-27 client Figma replaced the coloured message block that used
 * to sit under the address with a single green/red icon inside the input. The
 * verdict wording — and ZeroBounce's "did you mean" suggestion — moves into
 * the tooltip rather than being dropped, so an advisor can still find out WHY
 * an address failed. INFLOWS-188 blocking behaviour is untouched: an Invalid
 * result still stops Next via the step-blocker signal computed upstream.
 *
 *   - Invalid  → red exclamation
 *   - Warn     → amber warning (CatchAll / SpamTrap / DoNotMail)
 *   - Valid    → green check
 *   - Unknown  → red exclamation for an advisor, nothing in preview
 *   - Checking → spinner
 *
 * The Unknown split is why this needs `mode`. Rendering nothing was right while
 * a soft-failed lookup let the advisor through — no verdict, so imply none. Now
 * that it STOPS an advisor, a silent row would disable Next with nothing on
 * screen saying which address did it. Red rather than amber because red is the
 * band that means "this one is blocking you"; amber means "noted, carry on",
 * which is exactly what CatchAll next to it is doing.
 */
function EmailVerifyIcon({
  verifying,
  result,
  mode,
  t,
}: {
  verifying: boolean;
  result: EmailVerifyResult | undefined;
  mode: 'preview' | 'advisor';
  t: (key: string, opts?: Record<string, unknown>) => string;
}) {
  if (!result) {
    if (!verifying) return null;
    return (
      <Tooltip
        title={t('formRenderer.eSign.emailVerify.checking', { defaultValue: 'Checking email…' })}
      >
        <LoadingOutlined className="bmo-esign-table__status" style={{ color: '#6b7280' }} />
      </Tooltip>
    );
  }

  const kind = badgeKindFor(result.status, mode);
  if (kind === 'silent') return null;

  const palette = BADGE_PALETTE[kind];
  const Icon = palette.icon;
  // An advisor's Unknown gets its own wording: "unavailable" alone reads as
  // informational, and this one is the reason Next is dead.
  const labelKey =
    result.status === 'Unknown' && mode === 'advisor'
      ? 'formRenderer.eSign.emailVerify.unknownBlocking'
      : (LABEL_KEY_FOR_STATUS[result.status] ?? 'formRenderer.eSign.emailVerify.unknown');
  const label = t(labelKey, {
    defaultValue:
      result.status === 'Unknown' && mode === 'advisor'
        ? "We couldn't verify this email. Leave and re-enter this step to try again."
        : DEFAULT_LABEL_FOR_STATUS[result.status],
  });
  const suggestion = result.didYouMean
    ? t('formRenderer.eSign.emailVerify.didYouMean', {
        suggestion: result.didYouMean,
        defaultValue: 'Did you mean {{suggestion}}?',
      })
    : null;

  return (
    <Tooltip title={suggestion ? `${label} ${suggestion}` : label}>
      <Icon
        className="bmo-esign-table__status"
        style={{ color: palette.icon_color }}
        aria-label={label}
      />
    </Tooltip>
  );
}

/** Which visual band a status renders as. `silent` means don't render the badge
 *  at all — which now only happens for an Unknown in PREVIEW, where a
 *  soft-failed lookup still implies no verdict and blocks nothing. For an
 *  advisor the same status gates Next, so it has to be visible. */
function badgeKindFor(
  status: EmailVerifyStatus,
  mode: 'preview' | 'advisor',
): 'invalid' | 'warn' | 'valid' | 'silent' {
  if (status === 'Invalid') return 'invalid';
  if (status === 'SpamTrap' || status === 'DoNotMail' || status === 'CatchAll') return 'warn';
  if (status === 'Valid') return 'valid';
  return mode === 'advisor' ? 'invalid' : 'silent';
}

const BADGE_PALETTE: Record<
  'invalid' | 'warn' | 'valid',
  {
    bg: string;
    border: string;
    text: string;
    icon_color: string;
    icon: typeof ExclamationCircleFilled;
  }
> = {
  invalid: {
    bg: '#fef2f2',
    border: '#fecaca',
    text: '#991b1b',
    icon_color: '#dc2626',
    icon: ExclamationCircleFilled,
  },
  warn: {
    bg: '#fefce8',
    border: '#fde68a',
    text: '#854d0e',
    icon_color: '#a16207',
    icon: WarningFilled,
  },
  valid: {
    bg: '#f0fdf4',
    border: '#bbf7d0',
    text: '#166534',
    icon_color: '#16a34a',
    icon: CheckCircleFilled,
  },
};

const LABEL_KEY_FOR_STATUS: Record<EmailVerifyStatus, string> = {
  Valid: 'formRenderer.eSign.emailVerify.valid',
  Invalid: 'formRenderer.eSign.emailVerify.invalid',
  CatchAll: 'formRenderer.eSign.emailVerify.catchAll',
  SpamTrap: 'formRenderer.eSign.emailVerify.spamTrap',
  DoNotMail: 'formRenderer.eSign.emailVerify.doNotMail',
  Unknown: 'formRenderer.eSign.emailVerify.unknown',
};

const DEFAULT_LABEL_FOR_STATUS: Record<EmailVerifyStatus, string> = {
  Valid: 'Email verified.',
  Invalid: 'This email is not deliverable.',
  CatchAll: "Domain accepts anything — we can't confirm the mailbox.",
  SpamTrap: 'This address looks like a spam trap.',
  DoNotMail: 'This address is on a do-not-mail list.',
  Unknown: 'Verification unavailable.',
};

function EmptyCard({ message }: { message: string }) {
  return (
    <div
      style={{
        padding: '24px 18px',
        borderRadius: 10,
        background: '#f8fafc',
        border: '1px dashed #cbd5e1',
        textAlign: 'center',
      }}
    >
      <Text type="secondary" style={{ fontSize: 13 }}>
        {message}
      </Text>
    </div>
  );
}

// ---- Helpers ---------------------------------------------------------------

/**
 * The Role column's text for one signer.
 *
 *   - The advisor has no authored label (the domain keeps it null) and reads
 *     "Advisor", the wording used for them everywhere else on this step.
 *   - A role that auto-multiplies over a REPEATABLE section stands for one
 *     person per instance, so its rows are told apart by the section's instance
 *     label ("Accountholder 2") — the same header the advisor saw on the form.
 *   - Anything else — including a role pinned with `fixedInstanceIndex`, which
 *     the originator already named for its one instance — shows its authored
 *     label in the form language.
 */
export function signerRoleLabel(
  role: SignatoryRole,
  instanceIdx: number,
  template: WorkflowTemplate,
  sectionByField: Map<string, FieldSectionInfo>,
  language: 'en' | 'fr',
  t: (key: string) => string,
): string {
  if (role.type === 'advisor') return t('formRenderer.eSign.advisor');
  const authored =
    (language === 'fr' ? role.labelFr || role.labelEn : role.labelEn || role.labelFr) ?? '';
  if (role.fixedInstanceIndex != null) return authored;
  // The same identity slots `signerInstanceCount` multiplies on, in its order,
  // so the label names the section that actually produced this row.
  const m = role.fieldMappings;
  for (const id of [m?.nameFieldId, m?.lastNameFieldId, m?.emailFieldId, m?.phoneFieldId]) {
    const info = id ? sectionByField.get(id) : undefined;
    if (!info?.repeatable) continue;
    for (const step of template.steps) {
      if (step.type !== 'DynamicForm') continue;
      const section = step.sections.find((s) => s.id === info.id);
      if (section) return renderInstanceLabel(section, instanceIdx, language);
    }
  }
  return authored;
}
//
// The signer roster itself (visibility, instance expansion, contact resolution)
// lives in `signerRoster.ts` — shared with `buildESignSubmission` so the table
// rendered here and the package sent to BMO can never disagree about who signs.
